import * as React from 'react';
import { Helper } from '../../../../../Helper';
import DateTimeService from '../../../../../services/DateTimeService';
import * as styles from './CanteenMenuItem.css';
import { IAReader } from '../../../../reader/Reader';
import { IPlayNewsItem } from '../../../../../interfaces/IPlay';
import { LocalizationService } from '../../../../../services/LocalizationService';

export interface IProps {
  sizeUnit: number;
  textColor: string;
  items: IPlayNewsItem[]
  dayInMonth: string;
  dayOfTheWeek: string;
  isVertical: boolean;
  borderColor?: string;
  timeBatchBackgroundColor?: string;
  timeBatchTextColor?: string;
  timeBatchBorderColor?: string;
  eventCardColor?: string;
  eventCardTextColor?: string;
  showTitle?: boolean;
  showText?: boolean;
  showDate?: boolean;
  showTime?: boolean;
  showLocation?: boolean;
  showTimeBox?: boolean;
}

export interface IState {
  visibleCount?: number;
}

export class CanteenMenuItem extends React.Component<IProps, IState> {

  private readonly dateTimeService: DateTimeService = new DateTimeService();
  private readonly localizationService: LocalizationService = new LocalizationService();
  private itemId: string;
  private resizeObserver: ResizeObserver;

  constructor(props: IProps) {
    super(props);
    this.state = {
      visibleCount: undefined
    };
    this.itemId = Helper.getRandomStringKey();
  }

  public componentDidMount(): void {
    this.measureVisibleCount();
    const container = document.getElementById(`IA_canteenMenuItem_${this.itemId}`);
    if (container && typeof ResizeObserver !== "undefined") {
      this.resizeObserver = new ResizeObserver(() => this.setState({ visibleCount: undefined }));
      this.resizeObserver.observe(container);
    }
    if ((document as any).fonts?.ready) {
      (document as any).fonts.ready.then(() => this.setState({ visibleCount: undefined }));
    }
  }

  public componentWillUnmount(): void {
    this.resizeObserver?.disconnect();
  }

  public componentDidUpdate(prevProps: IProps): void {
    const layoutAffectingPropsChanged = prevProps.items !== this.props.items
      || prevProps.sizeUnit !== this.props.sizeUnit
      || prevProps.isVertical !== this.props.isVertical
      || prevProps.showDate !== this.props.showDate
      || prevProps.showTitle !== this.props.showTitle
      || prevProps.showText !== this.props.showText
      || prevProps.showTime !== this.props.showTime
      || prevProps.showLocation !== this.props.showLocation
      || prevProps.showTimeBox !== this.props.showTimeBox;
    if (layoutAffectingPropsChanged) {
      this.setState({ visibleCount: undefined });
      return;
    }
    if (this.state.visibleCount === undefined) {
      this.measureVisibleCount();
    }
  }

  // Renders all events first, then hides whichever don't fit and shows a "X more events" label instead
  private measureVisibleCount(): void {
    try {
      const container = document.getElementById(`IA_canteenMenuItem_${this.itemId}`);
      if (!container || this.props.items.length === 0) {
        return;
      }
      const containerRect = container.getBoundingClientRect();
      let availableHeight = containerRect.height;
      const headerDateDiv = document.getElementById(`IA_headerDate_${this.itemId}`);
      if (headerDateDiv) {
        availableHeight = containerRect.bottom - headerDateDiv.getBoundingClientRect().bottom;
      }
      const cards = container.getElementsByClassName(styles.IA_card);
      const cardSpacing = this.props.sizeUnit * 1.5; // matches the card's marginBottom, not included in offsetHeight
      let usedHeight = 0;
      let visibleCount = 0;
      for (let i = 0; i < cards.length; i++) {
        usedHeight += (cards[i] as HTMLElement).offsetHeight + cardSpacing;
        if (usedHeight > availableHeight) {
          break;
        }
        visibleCount++;
      }
      if (visibleCount < this.props.items.length) {
        const moreLabelHeight = this.props.sizeUnit * (this.props.isVertical ? 3 : 4);
        let heightWithLabel = moreLabelHeight;
        for (let i = 0; i < visibleCount; i++) {
          heightWithLabel += (cards[i] as HTMLElement).offsetHeight + cardSpacing;
        }
        while (visibleCount > 0 && heightWithLabel > availableHeight) {
          heightWithLabel -= (cards[visibleCount - 1] as HTMLElement).offsetHeight + cardSpacing;
          visibleCount--;
        }
      }
      this.setState({ visibleCount });
    } catch (error) {
      console.log(`%c${error}`, 'background: red; color: #ffffff')
    }
  }

  private formatText(): void {
    try {
      const contentBox = document.getElementById(`IA_text_${this.itemId}`);
      if (contentBox) {
        const links = contentBox.getElementsByTagName("a");
        if (links?.length > 0) {
          for (let linkIndex = 0; linkIndex < links.length; linkIndex++) {
            links[linkIndex].style.textDecoration = "none";
          }
        }
      }
    } catch (error) {
      console.log(`%c${error}`, 'background: red; color: #ffffff')
    }
  }

  public render(): JSX.Element {
    try {
      this.formatText();
    } catch (error) {
      console.log(`%c${error}`, 'background: red; color: #ffffff')
    }
    return (
      <div
        id={`IA_canteenMenuItem_${this.itemId}`}
        className={styles.IA_canteenMenuItem}
        style={{
          color: this.props.textColor
        }}
      >
        {this.props.showDate !== false &&
          <div
            id={`IA_headerDate_${this.itemId}`}
            style={{
              opacity: 0.7
            }}
          >
            <div
              style={{
                fontSize: this.props.sizeUnit * (this.props.isVertical ? 2 : 4),
              }}
            >
              {this.props.dayInMonth}
            </div>
            <div
              style={{
                fontSize: this.props.sizeUnit * (this.props.isVertical ? 0.75 : 1.5),
                marginBottom: this.props.sizeUnit * 2
              }}
            >
              {this.props.dayOfTheWeek?.toUpperCase()}
            </div>
          </div>
        }
        {this.props.items.slice(0, this.state.visibleCount ?? this.props.items.length).map((item, index) => {
          const accentColor = this.props.timeBatchBorderColor || this.props.borderColor || this.props.textColor;
          const showLocation = this.props.showLocation !== false && item.location;
          const showTime = this.props.showTime !== false && item.startDate;
          return (
            <div
              key={`item_${index}`}
              className={styles.IA_card}
              style={{
                backgroundColor: this.props.eventCardColor ? this.props.eventCardColor : "rgba(128, 128, 128, 0.12)",
                borderLeft: `${this.props.sizeUnit * 0.4}px solid ${accentColor}`,
                borderRadius: this.props.sizeUnit * 0.5,
                padding: this.props.sizeUnit * (this.props.isVertical ? 1 : 1.5),
                marginBottom: this.props.sizeUnit * 1.5,
                boxShadow: "0 1px 3px rgba(0, 0, 0, 0.15)",
                color: this.props.eventCardTextColor ? this.props.eventCardTextColor : this.props.textColor
              }}
            >
              {(showLocation || showTime) &&
                <div
                  className={styles.IA_cardHeader}
                  style={{
                    marginBottom: this.props.sizeUnit * 0.5
                  }}
                >
                  {showLocation &&
                    <div
                      className={styles.IA_cardHeaderLabel}
                      style={{
                        fontSize: this.props.sizeUnit * (this.props.isVertical ? 1 : 2),
                        color: accentColor
                      }}
                    >
                      {item.location.toUpperCase()}
                    </div>
                  }
                  {showTime &&
                    <div
                      className={styles.IA_cardHeaderMeta}
                      style={this.props.showTimeBox !== false ? {
                        fontSize: this.props.sizeUnit * (this.props.isVertical ? 0.9 : 1.8),
                        backgroundColor: this.props.timeBatchBackgroundColor,
                        color: this.props.timeBatchTextColor,
                        border: `1px solid ${this.props.timeBatchBorderColor ? this.props.timeBatchBorderColor : "transparent"}`,
                        borderRadius: this.props.sizeUnit * 2,
                        padding: `0 ${this.props.sizeUnit}px`
                      } : {
                        fontSize: this.props.sizeUnit * (this.props.isVertical ? 0.9 : 1.8),
                        opacity: 0.6
                      }}
                    >
                      {item.isAllDayEvent ? new LocalizationService().strings.DateTimeService_AllDay : `${this.dateTimeService.ConvertToHHMM(item.startDate)} - ${this.dateTimeService.ConvertToHHMM(item.endDate)}`}
                    </div>
                  }
                </div>
              }
              {this.props.showTitle !== false && item.title &&
                <div
                  id={`IA_title_${this.itemId}`}
                  className={styles.IA_title}
                  style={{
                    fontSize: this.props.sizeUnit * (this.props.isVertical ? 1 : 2),
                    marginBottom: this.props.sizeUnit * 0.8
                  }}
                >
                  {item.title}
                </div>
              }
              {this.props.showText !== false && item.content?.length > 0 &&
                <div
                  id={`IA_text_${this.itemId}`}
                  className={styles.IA_text}
                >
                  <IAReader
                    content={Helper.stripHtml(item.content)}
                    style={{
                      position: "relative",
                      borderWidth: 1,
                      wordBreak: "break-word",
                      fontSize: this.props.sizeUnit * (this.props.isVertical ? 1 : 2),
                      lineHeight: `${this.props.sizeUnit * (this.props.isVertical ? 1.25 : 2.5)}px`
                    }}
                  />
                </div>
              }
            </div>
          );
        })}
        {this.state.visibleCount !== undefined && this.state.visibleCount < this.props.items.length &&
          <div
            id={`IA_moreLabel_${this.itemId}`}
            className={styles.IA_moreLabel}
            style={{
              fontSize: this.props.sizeUnit * (this.props.isVertical ? 0.9 : 1.6),
              opacity: 0.7
            }}
          >
            {`${this.props.items.length - this.state.visibleCount} ${this.props.items.length - this.state.visibleCount > 1 ? this.localizationService.strings.Player_MoreEventsThisDay ?? "more events this day" : this.localizationService.strings.Player_MoreEventThisDay ?? "more event this day"}`}
          </div>
        }
      </div>
    )
  }


}